import { GeneralImageZoom, Link, Modal, ResponsiveImage, Tooltip, UserContext } from '@/components';
import { ImageLibrarySearchResult, UserRole } from '@/types';
import classNames from 'classnames';
import CheckboxBlankCircle from '@/assets/checkboxBlankCircle.svg';
import CheckboxCircle from '@/assets/checkboxCircle.svg';
import CornersOutLight from '@/assets/cornersOutLight.svg';
import FullscreenExit from '@/assets/fullscreenExit.svg';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import XLight from '@/assets/xLight.svg';
import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useFullscreen } from '@/utils';
import { useTranslations } from 'next-intl';
import ImageLibraryTags from './ImageLibraryTags';

const RESPONSIVE_IMAGE_WIDTHS = {
  xs: 200,
  sm: 320,
  md: 240,
  lg: 240,
  xl: 310,
  xxl: 340,
} as const;

const FULLSCREEN_IMAGE_WIDTHS = {
  xs: 960,
  sm: 1800,
  md: 2000,
  lg: 2500,
  xl: 3000,
} as const;

type ImageLibraryResultImageType = {
  libraryImage: ImageLibrarySearchResult;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  selected: boolean;
  onSelectImage: (imageLibrary: ImageLibrarySearchResult) => void;
  showSaveToScrapbook?: boolean;
  showDownloadBtn?: boolean;
};

const ImageLibraryResultImage = (data: ImageLibraryResultImageType) => {
  const { onImageLoad, showSaveToScrapbook, showDownloadBtn, selected, onSelectImage, libraryImage } = data;

  const [open, setOpen] = useState(false);
  const [imageFailedToLoad, setImageFailedToLoad] = useState(false);
  const { roles } = useContext(UserContext);
  const { isFullscreen, exitFullscreen, enterFullscreen } = useFullscreen();
  const t = useTranslations();
  const mainElementRef = useRef<HTMLDivElement>(null);

  const showCopyBtns = useMemo(() => roles?.includes(UserRole.Staff) ?? false, [roles]);

  const openImage = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, []);

  const openFullscreen = useCallback(() => {
    if (mainElementRef.current) {
      enterFullscreen(mainElementRef.current);
    }
  }, [enterFullscreen]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setImageFailedToLoad(true);
      onImageLoad(e);
    },
    [onImageLoad],
  );

  const handleSelectImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectImage(libraryImage);
    },
    [onSelectImage, libraryImage],
  );

  const fullscreenButton = useMemo(
    () => ({
      icon: <CornersOutLight className="h-6 w-6 !fill-black" />,
      tooltipText: t('imageGallery.$openFullscreen'),
      onClick: openFullscreen,
    }),
    [openFullscreen, t],
  );

  if (imageFailedToLoad) {
    return null;
  }

  return (
    <>
      <div className="group relative" onClick={openImage}>
        <ResponsiveImage
          className="h-auto w-full"
          image={libraryImage.media}
          widths={RESPONSIVE_IMAGE_WIDTHS}
          imgProps={{
            className: classNames('cursor-pointer rounded-md'),
            onLoad: onImageLoad,
            onError: handleImageError,
          }}
        />
        {(showSaveToScrapbook || showDownloadBtn) && (
          <button
            className={classNames(
              'absolute top-3 left-3 cursor-pointer transition-opacity duration-200 group-hover:opacity-100 pointer-fine:opacity-0',
              selected && 'opacity-100 pointer-fine:opacity-100',
            )}
            onClick={handleSelectImage}
          >
            {selected ? (
              <CheckboxCircle className="h-6 w-6" />
            ) : (
              <CheckboxBlankCircle className="h-6 w-6 fill-white stroke-black" />
            )}
          </button>
        )}

        <ImageLibraryTags tags={libraryImage.tags} />
      </div>
      <Modal
        open={open}
        onClose={handleCloseModal}
        contanerClassName="p-6 max-w-full max-h-full lg:max-h-[80%] lg:max-w-[80%] min-w-[300px] min-h-[300px] bg-white !rounded-none lg:!rounded-md disable-scroll"
      >
        <div ref={mainElementRef} className="flex h-full w-full items-center justify-center overflow-hidden">
          <GeneralImageZoom
            image={libraryImage.media}
            widths={FULLSCREEN_IMAGE_WIDTHS}
            visible
            showCopyrightText={false}
            disableClick
            setContanerSize={!isFullscreen}
            dataNotLazy
            buttons={isFullscreen ? undefined : [fullscreenButton]}
            showCopyBtns={showCopyBtns}
            showDownloadBtn={showDownloadBtn}
            className="[&_.buttons_.btn]:!opacity-100 [&_.caption]:max-w-[calc(100%-135px)] [&_.caption]:text-sm"
          />

          <div className="absolute top-9 left-9 z-10">
            <Tooltip placement="right" content={t('colourLibrary.$close')}>
              <button
                className="btn icon cursor-pointer rounded-full p-2 shadow-md transition-all"
                onClick={handleCloseModal}
              >
                <XLight className="h-6 w-6" />
              </button>
            </Tooltip>
          </div>

          {isFullscreen && (
            <div className="absolute right-3 bottom-3 z-10">
              <Tooltip placement="left" content={t('imageGallery.$exitFullscreen')}>
                <button
                  className="btn icon cursor-pointer rounded-full p-2 shadow-md transition-all"
                  onClick={exitFullscreen}
                >
                  <FullscreenExit className="h-6 w-6" />
                </button>
              </Tooltip>
            </div>
          )}

          {libraryImage.report && (
            <Link
              link={{ ...libraryImage.report.reportUrl, target: '_blank' }}
              className="btn border-gray-4 absolute right-9 bottom-9 flex !items-start border bg-white !py-1.5 !pr-3 !pl-3 !font-medium whitespace-nowrap shadow-md hover:bg-gray-100 disabled:!text-gray-400 disabled:hover:bg-white [&>svg]:!fill-black disabled:[&>svg]:!fill-gray-400"
            >
              <ArrowRightLight className="mt-0.5 h-5 w-5 -rotate-45 !fill-black" />
              <span className="mt-px hidden text-sm lg:block">{t('imageLibrary.$visitReport')}</span>
            </Link>
          )}

          <ImageLibraryTags tags={libraryImage.tags} className="!right-9 !bottom-20" />
        </div>
      </Modal>
    </>
  );
};

export default memo(ImageLibraryResultImage, (prevProps, nextProps) => {
  return (
    prevProps.libraryImage === nextProps.libraryImage &&
    prevProps.selected === nextProps.selected &&
    prevProps.onImageLoad === nextProps.onImageLoad &&
    prevProps.onSelectImage === nextProps.onSelectImage &&
    prevProps.showSaveToScrapbook === nextProps.showSaveToScrapbook &&
    prevProps.showDownloadBtn === nextProps.showDownloadBtn
  );
});
